'use client';

import React from 'react';
import { Text, Heading } from '@/components/ui/design-system';
import { cn } from '@/lib/utils';
import { Mail, Phone, MapPin } from 'lucide-react';

interface LegalContactInfoProps {
  className?: string;
  title: string;
  introText?: string; // Made optional as not all contexts might have it
  companyName: string;
  address: string;
  phone: string;
  phoneHref: string;
  email: string;
  emailHref: string;
}

/**
 * LegalContactInfo Component
 * 
 * Displays standardized contact information for legal pages
 * following the design system patterns.
 */
export function LegalContactInfo({
  className,
  title,
  introText,
  companyName,
  address,
  phone,
  phoneHref,
  email,
  emailHref,
}: LegalContactInfoProps) {
      return (
    <div className={cn('', className)}>
      <Heading level={2} className="heading-underline-animated pb-2 mb-3">
        {title}
      </Heading>
      {introText && <Text color="secondary" className="mt-4 mb-5">{introText}</Text>}
      <div className="mt-6 space-y-3">
        {/* Company & Address Block */}
        <div className="flex items-start">
          <MapPin className="h-5 w-5 text-primary mr-3 mt-1 flex-shrink-0" />
          <div>
            <Text weight="semibold">{companyName}</Text>
            <Text size="sm" color="muted">{address}</Text>
          </div>
        </div>
        
        {/* Phone Block */}
        <div className="flex items-center">
          <Phone className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
          <Text size="sm" color="secondary">
            <Text as="span" weight="semibold" color="default">Phone:</Text>{' '}
            <a href={phoneHref} className="text-primary hover:underline">{phone}</a>
          </Text>
        </div>
        
        {/* Email Block */}
        <div className="flex items-center">
          <Mail className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
          <Text size="sm" color="secondary">
            <Text as="span" weight="semibold" color="default">Email:</Text>{' '}
            <a href={emailHref} className="text-primary hover:underline">{email}</a>
          </Text>
        </div>
      </div>
    </div>
  );
}
